import React, { useContext, useState } from "react";
import "./Comment.css";
import { RESTComment } from "../services/types";
import { UserContext } from "../contexts/UserContext";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import { deleteComment } from "../services/comments";
import Button from "./Button";
import Avatar from "./Avatar";
import { TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { Link } from "react-router-dom";
import { formatFriendlyDate } from "../utils/dateFormat";

interface CommentProps {
    comment: RESTComment;
    onDeleted?: () => void;
    showPostLink?: boolean;
}

const Comment: React.FC<CommentProps> = ({ comment, onDeleted, showPostLink = false }) => {
    const { isModerator } = useContext(UserContext);
    const { execute: deleteCommentAction, loading: isDeleting } = useAuthenticatedAction();

    const handleDelete = async () => {
        if (!comment.id) return;
        
        const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?');
        if (!confirmed) return;

        try {
            await deleteCommentAction(deleteComment, comment.id);
            if (onDeleted) {
                onDeleted();
            }
        } catch (error) {
            console.error('Failed to delete comment:', error);
            alert('Échec de la suppression du commentaire.');
        }
    };

    return (
        <li className="Comment">
            <div className="comment_header">
                <div className="comment_meta">
                    <Avatar 
                        url={comment.author_avatar_urls?.["24"]} 
                        alt={comment.author_name || 'Avatar'}
                        size="small"
                    />
                    <span className="comment_author">
                        {comment.author_name}
                    </span>
                    {comment.date && (
                        <span className="comment_date">
                            <time dateTime={comment.date}>
                                {formatFriendlyDate(comment.date)}
                            </time>
                        </span>
                    )}
                    {showPostLink && comment.post && (
                        <Link to={`/post?p=${comment.post}`} className="comment_post_link">
                            <ArrowTopRightOnSquareIcon className="comment_post_link_icon" />
                            BD #{comment.post}
                        </Link>
                    )}
                </div>
                {isModerator() && (
                    <Button
                        variant="danger"
                        size="small"
                        iconOnly
                        loading={isDeleting}
                        onClick={handleDelete}
                        title="Supprimer le commentaire"
                        className="comment_delete"
                    >
                        <TrashIcon />
                    </Button>
                )}
            </div>
            <span 
                className="comment_content" 
                dangerouslySetInnerHTML={{
                    __html: (comment.content as {
                        rendered: string
                    }).rendered
                }}
            />
        </li>
    );
};

export default Comment;
